import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase/config';
import { collection, onSnapshot, doc, updateDoc, query, where, or } from 'firebase/firestore';
import toast from 'react-hot-toast'; 
import { Chess } from 'chess.js';
import ArcadeBoard from '../components/Arcade/ArcadeBoard';
import ChessBoard from '../components/ChessBoard';
import HungryPieceGame from '../components/HungryPieceGame';
import LiveVideo from '../components/LiveVideo';
import { PIECE_IMAGES, BOTS } from '../utils/AppConstants';
import iconPawn3d from '../assets/3d_icons/icon_pawn.png';
import iconKnight3d from '../assets/3d_icons/icon_knight.png';
import companionLeo from '../assets/3d_icons/companion_leo.png';
import companionSparky from '../assets/3d_icons/companion_sparky.png';
import companionMerlin from '../assets/3d_icons/companion_merlin.png';
import companionRocky from '../assets/3d_icons/companion_rocky.png';

import StudentHome from '../components/StudentTabs/StudentHome';
import ProfileSettings from '../components/ProfileSettings';
import SkeletonLoader from '../components/SkeletonLoader';
import { CartoonAvatar, XPBadge } from '../components/GamificationAssets';
import AvatarPicker from '../components/AvatarPicker';

const QUOTES = [
    "Every chess master was once a beginner. - Irving Chernev",
    "Chess is life in miniature. - Garry Kasparov",
    "Play the opening like a book, the middlegame like a magician, and the endgame like a machine. - Spielmann",
    "The hardest game to win is a won game. - Emanuel Lasker"
];

// Companion options reuse existing 3D assets

const MiniPreviewBoard = React.memo(({ fen }) => {
    const temp = new Chess(); try { temp.load(fen); } catch(e){}
    const rows = [8,7,6,5,4,3,2,1]; const cols = ['a','b','c','d','e','f','g','h'];
    const render = {}; temp.board().forEach((row, r) => row.forEach((sq, c) => { if(sq) render[`${cols[c]}${rows[r]}`] = sq.color + sq.type.toUpperCase(); }));
    return (
        <div style={{width: '100%', aspectRatio: '1/1', display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gridTemplateRows: 'repeat(8, 1fr)', border: '1px solid rgba(163, 177, 198, 0.4)', borderRadius: '6px', overflow: 'hidden', boxShadow: 'var(--neu-shadow-inset)' }}>
            {rows.map((row, rIdx) => cols.map((col, cIdx) => {
                const id = `${col}${row}`; const isDark = (rIdx + cIdx) % 2 === 1;
                return ( <div key={id} style={{background: isDark ? '#8ca2ad' : '#dee3e6', display: 'flex', justifyContent: 'center', alignItems: 'center' }}> {render[id] && <img src={PIECE_IMAGES[render[id]]} alt="p" style={{width: '90%' }} />} </div> );
            }))}
        </div>
    );
}, (prev, next) => prev.fen === next.fen);

export default function StudentDashboard({ user, joinedLiveClass, setJoinedLiveClass, setActiveTab: setGlobalActiveTab }) {
    const [myProfile, setMyProfile] = useState(null);
    const [allClasses, setAllClasses] = useState([]);
    const [myBatches, setMyBatches] = useState([]);
    const [myHomeworks, setMyHomeworks] = useState([]);
    const [globalSettings, setGlobalSettings] = useState(null);
    const [showRechargePopup, setShowRechargePopup] = useState(false); 
    const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
    const [delayedVideoActive, setDelayedVideoActive] = useState(false);
    const [usersMap, setUsersMap] = useState({});
    
    const [dashboardTab, setDashboardTab] = useState('home'); 
    const [quoteOfDay, setQuoteOfDay] = useState('');
    
    // 🔥 DYNAMIC DRAG-TO-RESIZE PANEL STATES & HANDLERS (DOM-RESILIENT REFS)
    const [notesPanelWidth, setNotesPanelWidth] = useState(520);
    const [isDraggingNotesPanel, setIsDraggingNotesPanel] = useState(false);
    const [videoHeight, setVideoHeight] = useState('200px');
    const [studentRightTab, setStudentRightTab] = useState('video');
    const [isMobileOrTablet, setIsMobileOrTablet] = useState(window.innerWidth <= 1024);

    useEffect(() => {
        const handleResize = () => setIsMobileOrTablet(window.innerWidth <= 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const liveClassData = allClasses.find(c => c.id === joinedLiveClass?.id);
    const videoActive = liveClassData?.videoActive || false;

    useEffect(() => {
        const unsub = onSnapshot(collection(db, "users"), (snap) => {
            const m = {}; snap.forEach(doc => { m[doc.id.toLowerCase()] = doc.data().name || doc.id; }); setUsersMap(m);
        });
        return () => unsub();
    }, []);

    // 🤖 AUTOMATED BOT OPPONENT MOVE LOGIC FOR ARENA PAIRINGS
    useEffect(() => {
        if (!liveClassData || liveClassData.classMode !== 'PAIRING') return;
        const activeGames = liveClassData.activeGames || {};
        const myGameEntry = Object.entries(activeGames).find(([gId, g]) => g.white === user.email || g.black === user.email);
        if (!myGameEntry) return;

        const [gameId, g] = myGameEntry;
        const isBotBlack = g.black.startsWith('bot_') || BOTS.some(b => b.id === g.black);
        
        if (isBotBlack && g.turn === 'b' && g.status === 'ACTIVE') {
            const botId = g.black;
            const botConfig = BOTS.find(b => b.id === botId) || BOTS[0];
            const executeBotMove = async () => {
                try {
                    const temp = new Chess(g.fen);
                    const allMoves = temp.moves({ verbose: true });
                    if (allMoves.length === 0) return;

                    let chosenMove = null;
                    const isRandom = Math.random() < botConfig.randomChance;
                    if (isRandom) {
                        chosenMove = temp.move(allMoves[Math.floor(Math.random() * allMoves.length)]);
                    } else {
                        let apiSuccess = false;
                        try {
                            const res = await fetch('https://chess-api.com/v1', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ fen: g.fen, depth: botConfig.depth })
                            });
                            if (res.ok) {
                                const data = await res.json();
                                if (data.san) {
                                    chosenMove = temp.move(data.san);
                                    apiSuccess = true;
                                }
                            }
                        } catch (err) {}
                        if (!apiSuccess || !chosenMove) {
                            chosenMove = temp.move(allMoves[Math.floor(Math.random() * allMoves.length)]);
                        }
                    }

                    if (chosenMove) {
                        const moves = g.pgn ? g.pgn.split(',').filter(Boolean) : [];
                        moves.push(chosenMove.san);
                        await updateDoc(doc(db, "classes", liveClassData.id), {
                            [`activeGames.${gameId}.fen`]: temp.fen(),
                            [`activeGames.${gameId}.pgn`]: moves.join(','),
                            [`activeGames.${gameId}.turn`]: 'w',
                            [`activeGames.${gameId}.lastMoveAt`]: Date.now()
                        });
                    }
                } catch (e) {
                    console.error("Bot move failed:", e);
                }
            };

            const timer = setTimeout(executeBotMove, 800); 
            return () => clearTimeout(timer);
        }
    }, [liveClassData?.activeGames, liveClassData?.classMode, user?.email]);

    useEffect(() => {
        if (videoActive) {
            const timer = setTimeout(() => {
                setDelayedVideoActive(true);
            }, 3000); // 3-second delay
            return () => clearTimeout(timer);
        } else {
            setDelayedVideoActive(false);
        }
    }, [videoActive]);

    const notesPanelRef = useRef(null);
    const videoContainerRef = useRef(null);

    const startNotesPanelResize = (e) => {
        e.preventDefault();
        setIsDraggingNotesPanel(true);
        const startX = e.clientX;
        const startWidth = notesPanelWidth;
        let currentWidth = startWidth;
        const doDrag = (moveEvent) => {
            const deltaX = moveEvent.clientX - startX;
            currentWidth = Math.max(200, Math.min(window.innerWidth - 650, startWidth - deltaX));
            setNotesPanelWidth(currentWidth);
        };
        const stopDrag = () => {
            setIsDraggingNotesPanel(false);
            window.removeEventListener('pointermove', doDrag);
            window.removeEventListener('pointerup', stopDrag);
        };
        window.addEventListener('pointermove', doDrag);
        window.addEventListener('pointerup', stopDrag);
    };

    const startVideoResizeFromEdge = (edge) => (e) => {
        e.preventDefault();
        const isHorizontal = edge === 'left' || edge === 'right';
        if (isHorizontal) setIsDraggingNotesPanel(true);
        const startX = e.clientX || (e.touches && e.touches[0].clientX);
        const startY = e.clientY || (e.touches && e.touches[0].clientY);
        const startHeight = parseInt(videoHeight) || 200;
        const startWidth = notesPanelWidth;
        let currentHeight = startHeight;
        let currentWidth = startWidth;

        const doDrag = (moveEvent) => {
            const currentX = moveEvent.clientX || (moveEvent.touches && moveEvent.touches[0].clientX);
            const currentY = moveEvent.clientY || (moveEvent.touches && moveEvent.touches[0].clientY);
            if (currentX === undefined || currentY === undefined) return;

            if (edge === 'bottom' || edge === 'top') {
                const deltaY = edge === 'bottom' ? (currentY - startY) : (startY - currentY);
                currentHeight = Math.max(80, Math.min(500, startHeight + deltaY));
                setVideoHeight(`${currentHeight}px`);
            } else if (edge === 'left' || edge === 'right') {
                const deltaX = edge === 'left' ? (startX - currentX) : (currentX - startX);
                currentWidth = Math.max(200, Math.min(window.innerWidth - 650, startWidth + deltaX));
                setNotesPanelWidth(currentWidth);
            }
        };

        const stopDrag = () => {
            if (isHorizontal) setIsDraggingNotesPanel(false);
            window.removeEventListener('pointermove', doDrag);
            window.removeEventListener('pointerup', stopDrag);
        };
        window.addEventListener('pointermove', doDrag);
        window.addEventListener('pointerup', stopDrag);
    };

    const handleUpdateAvatar = async (url) => {
        if (!user?.email) return;
        const safeEmail = user.email.toLowerCase();
        try {
            await updateDoc(doc(db, "users", safeEmail), { avatar: url });
            toast.success(`Avatar updated successfully! ✨`);
            setIsAvatarModalOpen(false);
        } catch(e) {
            toast.error("Failed to update avatar.");
        }
    };

    const toggleStudentStatus = async (statusType) => {
        if (!liveClassData || !user?.email) return;
        const safeEmail = user.email.toLowerCase().replace(/\./g, '_');
        const currentStatus = liveClassData.studentStatus?.[safeEmail];
        const newStatus = currentStatus === statusType ? null : statusType;
        
        await updateDoc(doc(db, "classes", liveClassData.id), {
            [`studentStatus.${safeEmail}`]: newStatus
        }).catch(e => console.error(e));
        
        if (newStatus === 'RAISED_HAND') {
            toast.success("Hand raised! Coach is notified. 🙋");
        } else if (newStatus === 'DONE') {
            toast.success("Marked as finished! Great job! ✅");
        } else {
            toast("Status cleared.");
        }
    };

    useEffect(() => {
        setQuoteOfDay(QUOTES[Math.floor(Math.random() * QUOTES.length)]);
    }, []);

    // 🌍 FIREBASE REAL-TIME SUBSCRIPTIONS
    useEffect(() => {
        if (!user?.email) return;
        const safeEmail = user.email.toLowerCase();

        const unsubUser = onSnapshot(doc(db, "users", safeEmail), (docSnap) => {
            if (docSnap.exists()) setMyProfile({ id: docSnap.id, ...docSnap.data() });
        });

        const qClasses = query(collection(db, "classes"), where("students", "array-contains", safeEmail));
        const unsubClasses = onSnapshot(qClasses, (snap) => {
            const list = [];
            snap.forEach(d => list.push({ id: d.id, ...d.data() }));
            setAllClasses(list);
        });

        const qBatches = query(collection(db, "batches"), where("students", "array-contains", safeEmail));
        const unsubBatches = onSnapshot(qBatches, (snap) => {
            const list = []; 
            snap.forEach(d => list.push({ id: d.id, ...d.data() }));
            setMyBatches(list);
        });
        
        const unsubGlobal = onSnapshot(doc(db, "academy_settings", "global"), (docSnap) => {
            if (docSnap.exists()) setGlobalSettings(docSnap.data());
        });

        return () => { unsubUser(); unsubClasses(); unsubBatches(); unsubGlobal(); };
    }, [user?.email]);

    // 📖 DEPENDENT HOMEWORK SUBSCRIPTION (Individual + Batch Homeworks)
    const myBatchIdsStr = JSON.stringify(myBatches.map(b => b.id).sort());
    useEffect(() => {
        if (!user?.email) return;
        const safeEmail = user.email.toLowerCase();
        
        const batchIds = JSON.parse(myBatchIdsStr);
        let qHW;
        
        if (batchIds.length > 0) {
            qHW = query(
                collection(db, "homeworks"),
                or(
                    where("assignedTo", "array-contains", safeEmail),
                    where("studentEmail", "==", safeEmail),
                    where("batchId", "in", batchIds)
                )
            );
        } else {
            qHW = query(
                collection(db, "homeworks"),
                or(
                    where("assignedTo", "array-contains", safeEmail),
                    where("studentEmail", "==", safeEmail)
                )
            );
        }

        const unsubHW = onSnapshot(qHW, (snap) => {
            const list = [];
            snap.forEach(d => list.push({ id: d.id, ...d.data() }));
            setMyHomeworks(list);
        });

        return () => unsubHW();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.email, myBatchIdsStr]);

    // 🚀 THE SMART WAY: AUTO-TELEPORTATION ENGINE
    useEffect(() => {
        if (!myProfile || allClasses.length === 0) return;
        
        const safeEmail = myProfile.email.toLowerCase();
        
        // Find if coach made any class LIVE for this student
        const activeLiveClass = allClasses.find(c => c.status === 'LIVE' && (c.students || []).includes(safeEmail));

        if (activeLiveClass) {
            // If there is a live class, and student hasn't joined it yet -> FORCE JOIN automatically!
            if (!joinedLiveClass || joinedLiveClass.id !== activeLiveClass.id) {
                toast.success("Coach started the class! Teleporting you to the classroom... 🚀", { duration: 4000 });
                setJoinedLiveClass(activeLiveClass);
            }
        } else if (joinedLiveClass) {
            // Optional: If coach ends the class, automatically kick them back to Dashboard
            const checkingClass = allClasses.find(c => c.id === joinedLiveClass.id);
            if (checkingClass && checkingClass.status !== 'LIVE') {
                toast("Class has ended. Returning to your dashboard.", { icon: '⏹️' });
                setJoinedLiveClass(null);
            }
        }
    }, [allClasses, myProfile, joinedLiveClass, setJoinedLiveClass]);

    if (!myProfile) return <SkeletonLoader />;

    const safeEmail = user.email.toLowerCase();

    // CartoonAvatar now handles all avatar rendering — initials-based, no emoji needed

    // 🔴 SMART LIVE CLASS RENDERER (NO REFRESH REQUIRED)
    if (joinedLiveClass) {
        const liveClassData = allClasses.find(c => c.id === joinedLiveClass.id);

        if (!liveClassData || liveClassData.status !== 'LIVE') {
            return <SkeletonLoader />; 
        }

        // 🎮 INSTANT ARCADE SWITCH (If student is in an active pairing game, keep them in it even if classMode switches to teaching)
        const activeGames = liveClassData.activeGames || {};
        const myGameEntry = Object.entries(activeGames).find(([gId, g]) => g.white?.toLowerCase() === user.email.toLowerCase() || g.black?.toLowerCase() === user.email.toLowerCase());

        if (myGameEntry) {
            const [gameId, g] = myGameEntry;
            const isWhite = g.white?.toLowerCase() === user.email.toLowerCase();
            const playerColor = isWhite ? 'WHITE' : 'BLACK';
            const oppId = isWhite ? g.black : g.white;
            const normOppId = oppId ? oppId.replace('bot-', 'bot_') : '';
            const opponentLabel = (normOppId.startsWith('bot_') || BOTS.some(b => b.id === normOppId || b.id === oppId))
                ? (BOTS.find(b => b.id === normOppId || b.id === oppId)?.name || oppId)
                : (usersMap[oppId?.toLowerCase()] || oppId?.split('@')[0]);

            return (
                <div style={{ width: '100%', height: 'calc(100vh - 40px)', background: 'var(--bg-secondary)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px', background: 'var(--bg-primary)', borderBottom: '1px solid rgba(163, 177, 198, 0.25)', flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '18px', animation: 'pulse 1.5s infinite' }}>⚔️</span>
                            <span style={{ fontWeight: 'bold', color: 'var(--text-primary)', fontSize: '14px' }}>Live Arena Match</span>
                        </div>
                        <div style={{ fontSize: '13px', color: '#ec4899', fontWeight: 'bold' }}>
                            You ({isWhite ? 'White' : 'Black'}) vs {opponentLabel}
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button onClick={() => setJoinedLiveClass(null)} style={{ padding: '6px 12px', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '11px' }}>Exit Class</button>
                        </div>
                    </div>

                    {/* Main Playing Area */}
                    <div style={{ flex: 1, display: 'flex', position: 'relative', overflow: 'hidden' }}>
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
                            <div style={{ width: '100%', maxWidth: '520px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {/* Opponent Info */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--bg-primary)', borderRadius: '8px', border: '1px solid rgba(163, 177, 198, 0.3)' }}>
                                    <span style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>👤 {opponentLabel}</span>
                                    <span style={{ color: 'var(--text-secondary)' }}>{g.turn !== (isWhite ? 'w' : 'b') ? '⏳ Thinking...' : '⚪ Your Turn'}</span>
                                </div>

                                {/* Chessboard */}
                                <ChessBoard 
                                    role={playerColor} 
                                    classroomId={liveClassData.id} 
                                    isFrozen={false} 
                                    userEmail={user.email} 
                                    isArenaMode={true}
                                    arenaGameId={gameId}
                                    rightPanelWidth={notesPanelWidth} 
                                />

                                {/* Player Info */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--bg-primary)', borderRadius: '8px', border: '1px solid rgba(163, 177, 198, 0.3)' }}>
                                    <span style={{ fontWeight: 'bold', color: '#2563eb' }}>👤 You ({usersMap[user.email.toLowerCase()] || user.email.split('@')[0]})</span>
                                    <span style={{ color: 'var(--text-secondary)' }}>{g.turn === (isWhite ? 'w' : 'b') ? '🟢 Active' : '⏳ Waiting'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Sidebar - Collapsible Video */}
                        <div ref={notesPanelRef} className="student-notes-panel" style={{ width: window.innerWidth > 1024 ? `${notesPanelWidth}px` : '100%', background: 'var(--bg-primary)', borderRadius: '16px 0 0 16px', overflow: 'visible', boxShadow: notesPanelWidth === 0 ? 'none' : 'var(--panel-shadow)', position: 'relative', transition: isDraggingNotesPanel ? 'none' : 'width 0.2s ease', display: 'flex', flexDirection: 'column' }}>
                            {window.innerWidth > 1024 && (
                                <div 
                                    onClick={() => setNotesPanelWidth(notesPanelWidth === 0 ? 520 : 0)}
                                    style={{
                                        position: 'absolute',
                                        left: '-24px',
                                        top: '20px',
                                        width: '24px',
                                        height: '40px',
                                        background: 'var(--bg-secondary)',
                                        border: '1px solid rgba(163, 177, 198, 0.4)',
                                        borderRight: 'none',
                                        borderRadius: '8px 0 0 8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        zIndex: 1001,
                                        boxShadow: '-2px 2px 5px rgba(0,0,0,0.1)',
                                        color: 'var(--text-primary)',
                                        fontSize: '12px'
                                    }}
                                    title={notesPanelWidth === 0 ? "Expand Video" : "Collapse Video"}
                                >
                                    {notesPanelWidth === 0 ? '◀' : '▶'}
                                </div>
                            )}
                            <div style={{
                                display: notesPanelWidth === 0 ? 'none' : 'flex',
                                flexDirection: 'column',
                                width: '100%',
                                height: '100%',
                                overflow: 'hidden',
                                flex: 1
                            }}>
                                <div style={{ flex: 1, padding: '15px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div style={{ fontWeight: 'bold', fontSize: '13px', color: 'var(--text-primary)' }}>🎥 Classroom Video Feed</div>
                                    <div style={{ flex: 1, minHeight: '180px', borderRadius: '12px', overflow: 'hidden' }}>
                                        {delayedVideoActive ? (
                                            <LiveVideo roomName={liveClassData.id} userName={user.email} role="STUDENT" height="100%" />
                                        ) : (
                                            <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', background: 'rgba(0,0,0,0.1)', color: 'var(--text-secondary)', fontSize: '11px', borderRadius: '8px', border: '1px dashed rgba(163,177,198,0.5)' }}>Video Call Idle</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (liveClassData.classMode === 'ARCADE_MONITOR') {
            return (
                <div style={{width: '100%', height: 'calc(100vh - 40px)', display: 'flex', justifyContent: 'center', alignItems: 'center', boxSizing: 'border-box', padding: '10px', position: 'relative' }}>
                    {liveClassData.meetLink && (
                        <a href={liveClassData.meetLink} target="_blank" rel="noreferrer" style={{position: 'absolute', top: '15px', right: '15px', padding: '10px 15px', background: '#10b981', color: 'white', textDecoration: 'none', borderRadius: '8px', fontWeight: 'bold', zIndex: 100, boxShadow: '0 4px 10px rgba(16, 185, 129, 0.4)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{animation: 'pulse 1.5s infinite' }}>🎥</span> Join Video
                        </a>
                    )}
                    <ArcadeBoard activeClass={liveClassData} userEmail={safeEmail} />
                </div>
            );
        }

        // 🍎 INSTANT BASICS SWITCH
        if (liveClassData.classMode === 'MINIGAME') {
            return (
                <div style={{width: '100%', height: 'calc(100vh - 40px)', display: 'flex', justifyContent: 'center', alignItems: 'center', boxSizing: 'border-box', background: 'var(--bg-primary)' }}>
                    <HungryPieceGame db={db} classroomId={liveClassData.id} userEmail={safeEmail} initialChapter={liveClassData.basicsPiece || 'Rook'} />
                </div>
            );
        }

        // ♟️ INSTANT CHESSBOARD SWITCH
        return (
            <div className="student-dashboard-container">
                <style>{`
                    .student-dashboard-container {
                        width: 100%;
                        height: 100%;
                        display: flex;
                        flex-direction: column;
                        padding: 10px;
                        box-sizing: border-box;
                        overflow: hidden;
                    }
                    @media (max-width: 1024px) {
                        .student-dashboard-container {
                            height: auto;
                            overflow: visible;
                        }
                    }
                    .student-live-container { display: flex; flex-direction: row; gap: 20px; align-items: stretch; max-width: 100%; flex: 1; min-height: 0; overflow: visible; box-sizing: border-box; padding: 16px 20px 20px 20px; }
                    .student-board-wrapper { flex: 1 1 auto; min-width: 0; height: 100%; overflow: visible; }
                    .student-notes-panel { width: 280px; flex-shrink: 0; display: flex; flex-direction: column; height: 100%; overflow: visible; box-sizing: border-box; } 
                    .video-resize-handle { position: absolute; background: transparent; transition: background 0.15s ease, box-shadow 0.15s ease; z-index: 1000; }
                    .video-resize-handle:hover { background: rgba(59, 130, 246, 0.4) !important; box-shadow: 0 0 6px rgba(59, 130, 246, 0.8); }
                    @media (max-width: 1024px) { 
                        .student-live-container { flex-direction: column; align-items: center; height: auto; overflow: visible; padding: 5px 0; } 
                        .student-board-wrapper { width: 100%; display: flex; justify-content: center; height: auto; overflow: visible; }
                        .student-notes-panel { height: 260px; width: 100%; overflow: hidden; margin-top: 10px; border-radius: 16px; border: 1px solid rgba(163, 177, 198, 0.3); }
                    }
                    @keyframes premium-breathing-wizard {
                        0% {
                            transform: translateY(0);
                        }
                        50% {
                            transform: translateY(-8px);
                        }
                        100% {
                            transform: translateY(0);
                        }
                    }
                    .premium-wizard-container {
                        animation: premium-breathing-wizard 4s ease-in-out infinite;
                        transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                        cursor: pointer;
                    }
                    .premium-wizard-container:hover {
                        transform: translateY(-12px) scale(1.08) !important;
                    }
                `}</style>
                
                <div style={{
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: isMobileOrTablet ? '8px' : '15px', 
                    background: 'var(--bg-primary)', 
                    padding: isMobileOrTablet ? '8px 12px' : '10px 20px', 
                    borderRadius: '12px', 
                    marginBottom: isMobileOrTablet ? '10px' : '15px', 
                    boxShadow: 'var(--neu-shadow)', 
                    overflowX: 'auto' 
                }}>
                    <div style={{color: '#ef4444', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', whiteSpace: 'nowrap' }}>
                        <span style={{animation: 'pulse 1.5s infinite' }}>🔴</span> LIVE CLASS
                    </div>
                    <div style={{width: '2px', height: '15px', background: 'rgba(163, 177, 198, 0.4)' }}></div>
                    <div style={{color: 'var(--text-primary)', fontSize: '14px', fontWeight: '800', whiteSpace: 'nowrap' }}>
                        {liveClassData.topic || liveClassData.batchTitle}
                    </div>
                    <div style={{width: '2px', height: '15px', background: 'rgba(163, 177, 198, 0.4)' }}></div>
                    <div style={{color: '#2563eb', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                         👨‍🏫 Coach {liveClassData.teacherId?.split('@')[0]}
                    </div>
                    
                    {(() => {
                        const safeEmail = user?.email?.toLowerCase().replace(/\./g, '_');
                        const myClassStatus = liveClassData.studentStatus?.[safeEmail];
                        return (
                            <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto', alignItems: 'center' }}>
                                <button 
                                    onClick={() => toggleStudentStatus('RAISED_HAND')} 
                                    style={{
                                        padding: '6px 12px', 
                                        background: myClassStatus === 'RAISED_HAND' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'var(--bg-primary)', 
                                        color: myClassStatus === 'RAISED_HAND' ? 'white' : 'var(--text-primary)', 
                                        border: 'none', 
                                        borderRadius: '8px', 
                                        fontWeight: 'bold', 
                                        cursor: 'pointer', 
                                        fontSize: '12px', 
                                        whiteSpace: 'nowrap',
                                        boxShadow: myClassStatus === 'RAISED_HAND' ? '0 4px 10px rgba(245, 158, 11, 0.3)' : 'var(--neu-shadow)',
                                        animation: myClassStatus === 'RAISED_HAND' ? 'pulse 1.5s infinite' : 'none'
                                    }}
                                >
                                    {myClassStatus === 'RAISED_HAND' ? '🙋 Hand Raised' : '🙋 Raise Hand'}
                                </button>

                                <button 
                                    onClick={() => toggleStudentStatus('DONE')} 
                                    style={{
                                        padding: '6px 12px', 
                                        background: myClassStatus === 'DONE' ? 'linear-gradient(135deg, #10b981, #059669)' : 'var(--bg-primary)', 
                                        color: myClassStatus === 'DONE' ? 'white' : 'var(--text-primary)', 
                                        border: 'none', 
                                        borderRadius: '8px', 
                                        fontWeight: 'bold', 
                                        cursor: 'pointer', 
                                        fontSize: '12px', 
                                        whiteSpace: 'nowrap',
                                        boxShadow: myClassStatus === 'DONE' ? '0 4px 10px rgba(16, 185, 129, 0.3)' : 'var(--neu-shadow)'
                                    }}
                                >
                                    {myClassStatus === 'DONE' ? '✅ Finished!' : '✅ Mark Done'}
                                </button>
                            </div>
                        );
                    })()}

                    {liveClassData.meetLink && (
                        <a href={liveClassData.meetLink} target="_blank" rel="noreferrer" style={{marginLeft: '10px', padding: '6px 12px', background: '#2563eb', color: 'white', textDecoration: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', boxShadow: '0 4px 10px rgba(37, 99, 235, 0.3)', whiteSpace: 'nowrap', transition: '0.2s' }}>
                            🎥 Join Video Room
                        </a>
                    )}
                    <button onClick={() => setJoinedLiveClass(null)} style={{padding: '6px 12px', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap', marginLeft: '10px', boxShadow: 'var(--neu-shadow)' }}>
                        🏠 Return to Dashboard
                    </button>
                </div>
 
                <div className="student-live-container">
                    <div className="student-board-wrapper">
                        <ChessBoard role="STUDENT" classroomId={liveClassData.id} isFrozen={true} userEmail={user.email} rightPanelWidth={notesPanelWidth} />
                    </div>
                     <div ref={notesPanelRef} className="student-notes-panel" style={{ width: window.innerWidth > 1024 ? `${notesPanelWidth}px` : '100%', background: 'var(--bg-primary)', borderRadius: '16px', overflow: 'visible', boxShadow: notesPanelWidth === 0 ? 'none' : 'var(--panel-shadow)', position: 'relative', transition: isDraggingNotesPanel ? 'none' : 'width 0.2s ease' }}>
                        
                        {window.innerWidth > 1024 && (
                            <div 
                                onClick={() => setNotesPanelWidth(notesPanelWidth === 0 ? 520 : 0)}
                                style={{
                                    position: 'absolute',
                                    left: '-24px',
                                    top: '20px',
                                    width: '24px',
                                    height: '40px',
                                    background: 'var(--bg-secondary)',
                                    border: '1px solid rgba(163, 177, 198, 0.4)',
                                    borderRight: 'none',
                                    borderRadius: '8px 0 0 8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    zIndex: 1001,
                                    boxShadow: '-2px 2px 5px rgba(0,0,0,0.1)',
                                    color: 'var(--text-primary)',
                                    fontSize: '12px'
                                }}
                                title={notesPanelWidth === 0 ? "Expand Panel" : "Collapse Panel"}
                            >
                                {notesPanelWidth === 0 ? '◀' : '▶'}
                            </div>
                        )}

                        {/* Vertical Drag Handle */}
                        {window.innerWidth > 1024 && notesPanelWidth > 0 && (
                            <div 
                                onPointerDown={startNotesPanelResize}
                                style={{
                                    position: 'absolute',
                                    left: '-5px',
                                    top: 0,
                                    bottom: 0,
                                    width: '10px',
                                    cursor: 'col-resize',
                                    zIndex: 1000,
                                    background: 'transparent',
                                    transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = 'rgba(59, 130, 246, 0.4)'}
                                onMouseLeave={(e) => e.target.style.background = 'transparent'}
                            />
                        )}

                        <div style={{
                            display: notesPanelWidth === 0 ? 'none' : 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            height: '100%',
                            overflow: 'hidden'
                        }}>
                            {/* 📱 TAB HEADER (Mobile/Tablet Only) */}
                            {isMobileOrTablet && (
                                <div style={{ display: 'flex', background: 'var(--bg-primary)', borderBottom: '1px solid rgba(163, 177, 198, 0.3)', borderRadius: '16px 16px 0 0', flexShrink: 0 }}>
                                    <button 
                                        onClick={() => setStudentRightTab('video')} 
                                        style={{ 
                                            flex: 1, 
                                            padding: '10px', 
                                            background: 'transparent', 
                                            border: 'none', 
                                            fontWeight: 'bold', 
                                            fontSize: '13px',
                                            color: studentRightTab === 'video' ? '#2563eb' : 'var(--text-secondary)', 
                                            borderBottom: studentRightTab === 'video' ? '3px solid #2563eb' : '3px solid transparent', 
                                            cursor: 'pointer',
                                            outline: 'none',
                                            fontFamily: 'inherit'
                                        }}
                                    >
                                        🎥 Coach Video
                                    </button>
                                    <button 
                                        onClick={() => setStudentRightTab('notes')} 
                                        style={{ 
                                            flex: 1, 
                                            padding: '10px', 
                                            background: 'transparent', 
                                            border: 'none', 
                                            fontWeight: 'bold', 
                                            fontSize: '13px',
                                            color: studentRightTab === 'notes' ? '#2563eb' : 'var(--text-secondary)', 
                                            borderBottom: studentRightTab === 'notes' ? '3px solid #2563eb' : '3px solid transparent', 
                                            cursor: 'pointer',
                                            outline: 'none',
                                            fontFamily: 'inherit'
                                        }}
                                    >
                                        📝 Class Notes
                                    </button>
                                </div>
                            )}
 
                            {/* 📹 JITSI LIVE VIDEO CALL CONTAINER */}
                            <div style={{ padding: isMobileOrTablet ? '8px 12px' : '12px 18px', borderBottom: isMobileOrTablet ? 'none' : '1px solid rgba(163, 177, 198, 0.3)', background: 'transparent', width: '100%', boxSizing: 'border-box', position: 'relative', display: (isMobileOrTablet && studentRightTab !== 'video') ? 'none' : 'block' }}>
                                {!isMobileOrTablet && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-primary)', fontWeight: '800', fontSize: '13px' }}>
                                            <span>🎥 Video Feed</span>
                                        </div>
                                        {/* Sizing Controls */}
                                        <div style={{ display: 'flex', gap: '4px' }}>
                                            {[
                                                { label: '🔇', val: '0px', title: 'Hide' },
                                                { label: '📱', val: '180px', title: 'Small' },
                                                { label: '💻', val: '300px', title: 'Medium' },
                                                { label: '🖥️', val: '450px', title: 'Large' }
                                            ].map(p => (
                                                <button 
                                                    key={p.val}
                                                    onClick={() => setVideoHeight(p.val)}
                                                    className="kid-btn-bubble"
                                                    title={p.title}
                                                    style={{
                                                        padding: '4px 8px', borderRadius: '6px', fontSize: '10px', border: 'none', cursor: 'pointer', fontWeight: 'bold',
                                                        background: videoHeight === p.val ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'var(--bg-primary)',
                                                        color: videoHeight === p.val ? 'white' : 'var(--text-secondary)',
                                                        boxShadow: videoHeight === p.val ? '0 2px 5px rgba(37, 99, 235, 0.3)' : 'var(--neu-shadow)',
                                                        outline: 'none'
                                                    }}
                                                >
                                                    {p.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                <div 
                                    ref={videoContainerRef}
                                    style={{ 
                                        height: isMobileOrTablet ? '205px' : (videoHeight !== '0px' ? videoHeight : 'auto'), 
                                        display: videoHeight === '0px' ? 'none' : 'block', 
                                        width: '100%',
                                        position: 'relative',
                                        borderRadius: '8px',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {delayedVideoActive ? (
                                        <LiveVideo roomName={liveClassData.id} userName={user.email} role="STUDENT" height="100%" />
                                    ) : (
                                        <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', background: 'rgba(0,0,0,0.15)', borderRadius: '8px', color: 'var(--text-secondary)', fontSize: '11px', fontStyle: 'italic', border: '1px dashed rgba(163, 177, 198, 0.5)', minHeight: '120px', boxSizing: 'border-box' }}>
                                            🎥 Waiting for coach to start the video call...
                                        </div>
                                    )}
                                </div>
 
                                {/* 4 Edge Drag Handles for Video Call Container */}
                                {!isMobileOrTablet && videoHeight !== '0px' && (
                                    <>
                                        <div onPointerDown={startVideoResizeFromEdge('top')} className="video-resize-handle" style={{ top: '-6px', left: 0, right: 0, height: '12px', cursor: 'ns-resize' }} title="Drag top edge to resize video height" />
                                        <div onPointerDown={startVideoResizeFromEdge('bottom')} className="video-resize-handle" style={{ bottom: '-6px', left: 0, right: 0, height: '12px', cursor: 'ns-resize' }} title="Drag bottom edge to resize video height" />
                                        <div onPointerDown={startVideoResizeFromEdge('left')} className="video-resize-handle" style={{ left: '-6px', top: 0, bottom: 0, width: '12px', cursor: 'ew-resize' }} title="Drag left edge to resize panel width" />
                                        <div onPointerDown={startVideoResizeFromEdge('right')} className="video-resize-handle" style={{ right: '-6px', top: 0, bottom: 0, width: '12px', cursor: 'ew-resize' }} title="Drag right edge to resize panel width" />
                                    </>
                                )}
                            </div>
  
                            {/* 📝 COACH NOTES CONTAINER */}
                            <div style={{ display: (!isMobileOrTablet || studentRightTab === 'notes') ? 'flex' : 'none', flexDirection: 'column', flex: 1, minHeight: 0, height: isMobileOrTablet ? '210px' : 'auto' }}>
                                {!isMobileOrTablet && (
                                    <div style={{background: 'transparent', padding: '20px', borderBottom: '1px solid rgba(163, 177, 198, 0.3)', color: 'var(--text-primary)', fontWeight: '800', fontSize: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span style={{fontSize: '24px' }}>📝</span> Coach's Notes
                                    </div>
                                )}
                                <div 
                                    dangerouslySetInnerHTML={{ 
                                        __html: liveClassData.whiteboardText || `<span style="color: var(--text-secondary); font-style: italic;">Waiting for coach to write something... ✍️</span>` 
                                    }} 
                                    style={{
                                        padding: isMobileOrTablet ? '10px 15px' : '25px 30px', 
                                        flex: 1, 
                                        color: 'var(--text-primary)', 
                                        whiteSpace: 'pre-wrap', 
                                        overflowY: 'auto', 
                                        fontSize: isMobileOrTablet ? '15px' : '18px', 
                                        lineHeight: '1.6', 
                                        letterSpacing: '0.5px' 
                                    }} 
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const xpPercent = (myProfile.totalXp || 0) % 100;

    return (
        <div style={{ width: '100%', minHeight: '100%', position: 'relative', overflow: 'visible', padding: '15px 10px', boxSizing: 'border-box' }}>
            {/* 🛸 TOY SPACESHIP CONSOLE CONTROLLER */}
            <div style={{ position: 'relative', zIndex: 10, maxWidth: '1200px', margin: '0 auto' }}>
                {/* 🎮 MAIN DASHBOARD PROFILE CONTROLLER PANEL */}
                <div className="glass-panel" style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    gap: '24px', 
                    padding: '24px 30px', 
                    marginBottom: '40px',
                    border: '3px solid rgba(255, 255, 255, 0.95)',
                    boxShadow: '0 15px 35px rgba(37, 99, 235, 0.25)',
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
                    overflow: 'visible',
                    position: 'relative',
                    zIndex: 50,
                    borderRadius: '28px'
                }}>
                    
                    {/* 👤 GAMIFIED CARTOON AVATAR + PROFILE */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '18px', minWidth: '260px' }}>
                        <CartoonAvatar
                            name={myProfile.name || 'Student'}
                            size={88}
                            avatarUrl={myProfile?.avatar}
                            showBadge={true}
                            onClick={() => setIsAvatarModalOpen(true)}
                            style={{ animation: 'float-cute 3.5s ease-in-out infinite', cursor: 'pointer' }}
                        />
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <span style={{ 
                                    background: 'rgba(255,255,255,0.22)', 
                                    color: '#fff', 
                                    fontSize: '10px', 
                                    fontWeight: 900, 
                                    textTransform: 'uppercase', 
                                    padding: '3px 10px', 
                                    borderRadius: '30px', 
                                    letterSpacing: '0.5px',
                                    border: '1px solid rgba(255,255,255,0.4)',
                                }}>♟ Hero Student</span>
                                <XPBadge xp={myProfile.totalXp || 0} size="sm" color="gold" />
                            </div>
                            <h2 style={{ margin: '6px 0 4px 0', color: '#fff', fontSize: '22px', fontWeight: 900, textShadow: '0 2px 4px rgba(0,0,0,0.2)', lineHeight: 1.2 }}>
                                {myProfile.name}
                            </h2>
                            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', display: 'inline-block', boxShadow: '0 0 6px #10b981' }} />
                                Academy Champion
                            </span>
                        </div>
                    </div>

                    {/* 🐴 CHESS KNIGHT XP LEVEL PROGRESS TRAIL */}
                    <div style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 5px' }}>
                            <span style={{ color: '#ffffff', fontWeight: '900', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" fill="currentColor"></path><line x1="4" y1="22" x2="4" y2="15"></line></svg>
                                Level {myProfile.level || 1}
                            </span>
                            <span style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '11px', fontWeight: 'bold' }}>
                                {xpPercent}/100 XP
                            </span>
                            <span style={{ color: '#fde047', fontWeight: '900', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="#fde047" stroke="#fde047" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                                Level {(myProfile.level || 1) + 1}
                            </span>
                        </div>
                        
                        {/* THE TRACK */}
                        <div style={{ 
                            height: '18px', 
                            background: 'rgba(255, 255, 255, 0.3)', 
                            borderRadius: '20px', 
                            position: 'relative', 
                            boxShadow: 'inset 2px 2px 5px rgba(0,0,0,0.1)',
                            border: '1.5px solid rgba(255, 255, 255, 0.45)',
                            padding: '2px',
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            {/* Visual Progress bar inside */}
                            <div style={{ 
                                width: `${xpPercent}%`, 
                                height: '100%', 
                                background: 'linear-gradient(90deg, #38bdf8, #60a5fa)', 
                                borderRadius: '20px',
                                transition: 'width 0.8s ease'
                            }}></div>
                            
                            {/* Sliding Knight Chess Piece */}
                            <div style={{ 
                                position: 'absolute', 
                                left: `calc(${xpPercent}% - 14px)`, 
                                transition: 'left 0.8s ease',
                                display: 'flex',
                                alignItems: 'center',
                                pointerEvents: 'none'
                            }}>
                                <img src={iconKnight3d} alt="Knight" style={{ width: '28px', height: '28px', objectFit: 'contain' }} />
                            </div>
                        </div>
                        <div style={{ textAlign: 'center', fontSize: '10px', color: 'rgba(255, 255, 255, 0.85)', fontWeight: 'bold' }}>
                            🚀 Just {100 - xpPercent} XP to Level-Up! (Total: {myProfile.totalXp || 0} XP)
                        </div>
                    </div>

                    {/* 💎 CHESS GEMS (CREDITS SAFE BOX) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div className="glass-panel" style={{ 
                            background: 'rgba(255, 255, 255, 0.2)', 
                            padding: '10px 18px', 
                            borderRadius: '16px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '12px', 
                            boxShadow: 'inset 2px 2px 5px rgba(0,0,0,0.1), 2px 2px 5px rgba(0,0,0,0.1)',
                            border: '1.5px solid rgba(255, 255, 255, 0.5)'
                        }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                <span style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '10px', textTransform: 'uppercase', fontWeight: 'bold' }}>
                                    Chess Gems
                                </span>
                                <span style={{ color: '#ffffff', fontSize: '18px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', marginRight: '4px', verticalAlign: 'middle' }}><polygon points="6 3 18 3 22 9 12 22 2 9 6 3" fill="#60a5fa" /></svg>
                                    {myProfile.classCredits ?? 0}
                                </span>
                            </div>
                            <button 
                                onClick={() => setShowRechargePopup(!showRechargePopup)} 
                                className="kid-btn-bubble"
                                style={{ 
                                    width: '32px', 
                                    height: '32px', 
                                    borderRadius: '50%', 
                                    background: '#10b981', 
                                    color: '#fff', 
                                    border: 'none', 
                                    cursor: 'pointer', 
                                    fontSize: '18px', 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center', 
                                    boxShadow: '0 2px 5px rgba(16,185,129,0.3)',
                                    fontWeight: 'bold'
                                }}
                            >
                                +
                            </button>
                        </div>

                        {/* SUPPORT POPUP */}
                        {showRechargePopup && (
                            <div className="glass-panel" style={{ 
                                position: 'absolute', 
                                top: '75px', 
                                right: '70px', 
                                background: 'var(--bg-primary)', 
                                padding: '20px', 
                                borderRadius: '16px', 
                                zIndex: 100, 
                                width: '300px', 
                                border: '1px solid rgba(163, 177, 198, 0.4)', 
                                boxShadow: 'var(--panel-shadow)', 
                                animation: 'sleekPop 0.3s ease' 
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                    <h4 style={{ margin: 0, color: 'var(--text-primary)' }}>💎 Recharge Gems</h4>
                                    <button onClick={() => setShowRechargePopup(false)} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 'bold' }}>✖</button>
                                </div>
                                <p style={{ margin: '0 0 5px 0', fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>Official UPI ID</p>
                                <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                                    <div style={{ flex: 1, background: 'var(--bg-primary)', padding: '8px 12px', borderRadius: '8px', color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '13px', border: '1px solid rgba(163, 177, 198, 0.2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={globalSettings?.upiId || 'Pending Assignment'}>
                                        {globalSettings?.upiId || 'Pending Assignment'}
                                    </div>
                                    <button 
                                        onClick={() => {
                                            const upi = globalSettings?.upiId || 'Pending Assignment';
                                            navigator.clipboard.writeText(upi);
                                            toast.success('UPI ID Copied! 📋', { icon: '💎' });
                                        }}
                                        style={{ background: '#2563eb', color: 'white', border: 'none', padding: '0 10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}
                                    >
                                        Copy
                                    </button>
                                </div>
                                <p style={{ margin: '0 0 5px 0', fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>Support Contact</p>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <div style={{ flex: 1, color: 'var(--text-primary)', fontSize: '13px', background: 'var(--bg-primary)', padding: '8px 12px', borderRadius: '8px', fontWeight: 'bold', border: '1px solid rgba(163, 177, 198, 0.2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={globalSettings?.supportContact || 'Pending Contact'}>
                                        {globalSettings?.supportContact || 'Pending Contact'}
                                    </div>
                                    {globalSettings?.supportContact && (
                                        <button 
                                            onClick={() => {
                                                const cleanContact = globalSettings.supportContact.replace(/[^0-9]/g, '');
                                                window.open(`https://wa.me/${cleanContact}?text=Hello,%20I%20want%20to%20recharge%20gems%20for%20my%20child's%20account.`, '_blank');
                                            }}
                                            style={{ background: '#10b981', color: 'black', border: 'none', padding: '0 10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}
                                        >
                                            Chat
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        {/* ⚙️ SHINY SETTINGS GEAR */}
                        <button 
                            onClick={() => setDashboardTab(dashboardTab === 'settings' ? 'home' : 'settings')} 
                            className="kid-btn-bubble"
                            style={{ 
                                width: '44px', 
                                height: '44px', 
                                borderRadius: '50%', 
                                background: 'rgba(255, 255, 255, 0.25)', 
                                color: '#ffffff',
                                cursor: 'pointer', 
                                fontSize: '20px', 
                                display: 'flex', 
                                justifyContent: 'center', 
                                alignItems: 'center', 
                                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                                transition: '0.2s',
                                border: '2px solid rgba(255, 255, 255, 0.6)'
                            }}
                            title={dashboardTab === 'settings' ? "Return Home" : "Settings"}
                        >
                            {dashboardTab === 'settings' ? (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                            ) : (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="animate-spin-slow"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
                            )}
                        </button>
                    </div>
                </div>

                {/* 🧙‍♂️ WIZARD SPEECH BUBBLE */}
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    marginBottom: '40px', 
                    padding: '0 20px', 
                    position: 'relative' 
                }}>
                    {/* Merlin 3D Character sitting on top */}
                    <div className="premium-wizard-container" style={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        marginBottom: '-12px',
                        zIndex: 3
                    }}>
                        <img 
                            src={companionMerlin} 
                            alt="Wizard" 
                            style={{ width: '64px', height: '64px', objectFit: 'contain' }} 
                        />
                    </div>

                    {/* Speech Bubble */}
                    <div className="kid-card" style={{ 
                        background: 'var(--bg-primary)', 
                        padding: '25px 40px 20px 40px', 
                        borderRadius: '28px', 
                        color: 'var(--text-primary)', 
                        fontStyle: 'italic', 
                        fontSize: '15px', 
                        zIndex: 2, 
                        boxShadow: 'var(--neu-shadow)', 
                        fontWeight: '700',
                        border: '3.5px solid #ffffff',
                        textAlign: 'center',
                        maxWidth: '650px',
                        position: 'relative'
                    }}>
                        {/* Speech Bubble Arrow pointing up to Merlin */}
                        <div style={{
                            position: 'absolute',
                            top: '-9px',
                            left: '50%',
                            transform: 'translateX(-50%) rotate(45deg)',
                            width: '16px',
                            height: '16px',
                            background: 'var(--bg-primary)',
                            borderLeft: '3.5px solid #ffffff',
                            borderTop: '3.5px solid #ffffff',
                            zIndex: 1
                        }}></div>
                        
                        <div style={{ position: 'relative', zIndex: 2 }}>
                            {(() => {
                                const parts = (quoteOfDay || "").split(" - ");
                                const quoteText = parts[0];
                                const quoteAuthor = parts[1];
                                return (
                                    <>
                                        <div>" {quoteText} "</div>
                                        {quoteAuthor && (
                                            <div style={{ 
                                                fontSize: '12px', 
                                                color: '#3b82f6', 
                                                marginTop: '10px', 
                                                fontWeight: '800', 
                                                textTransform: 'uppercase', 
                                                letterSpacing: '0.5px',
                                                fontStyle: 'normal'
                                            }}>
                                                — {quoteAuthor}
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>

                {/* MAIN CONTENT AREA */}
                <div style={{ animation: 'softFadeUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}>
                    {dashboardTab === 'home' && <StudentHome myBatches={myBatches} allClasses={allClasses} myHomeworks={myHomeworks} myProfile={myProfile} joinLiveClass={setJoinedLiveClass} globalSettings={globalSettings} setActiveTab={setGlobalActiveTab} />}
                    {dashboardTab === 'settings' && <ProfileSettings myProfile={myProfile} />}
                </div>

            </div>

            {isAvatarModalOpen && (
                <AvatarPicker 
                    currentUrl={myProfile?.avatar} 
                    onSelect={(url) => handleUpdateAvatar(url)} 
                    onClose={() => setIsAvatarModalOpen(false)} 
                />
            )}

        </div>
    );
}
